/*
 * Authors: mariushirjanu, Triadi
 * Created: 13/01/2022
 */
import * as _ from 'underscore';
import { util } from '../../../shared/util';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { UntypedFormGroup } from '@angular/forms';
import { IWebshopAccessSettings, IWebshopAccessSettingsRequest, WebshopAccessSettingsDataService } from './webshop-access-settings-data.service';
import { ICompany } from '../../../../type_definitions/datatypes';
import { WebshopService } from '../webshop.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import { UserDataService } from '../../../shared/user/user-data.service';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { SEC_WS_ACCESS, SEC_WS_RECREATEGENERICUSERS } from '../../../shared/services/auth/security-objects';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { DialogSize, GaMessageService, IDialog, MessageType } from '../../../shared/message/ga-message.service';
import { CopyToClipboardComponent } from '../../../shared/copy-to-clipboard/copy-to-clipboard.component';
import { ButtonAddonComponent } from '../../../shared/formly/addons/button-addon.component';
import { EmailTesterPopupComponent } from './email-tester-popup.component';
import { UserApprovalRadioFieldsComponent } from './user-approval-radio-fields.component';
import { SSOSectionFieldsComponent } from './sso-section-fields.component';
import { GaEventService } from '../../../shared/services/ga-event.service';
import { UserManualApprovalFieldComponent } from './user-manual-approval-field.component';
import { UserAutoApprovalFieldComponent } from './user-auto-approval-field.component';
import { Subscription } from 'rxjs';
import { GaWizard } from '../../../shared/wizard/ga-wizard';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';
import { WIZARD_WEBSHOP } from '../../../shared/app-wizards';
import { EVENT_REPORT_WRAPPER_REFRESH } from '../../../shared/report/report-wrapper.component';

const SAVE_ACCESS_ACTION:string = 'SAVE_ACCESS';

@Component({
  selector: 'webshop-access-settings',
  template:
    `
      <h2>
        <text key="HEADING"></text>
      </h2>
      <div *ngIf="!loading">
        <formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
        
        <div class="margin-top-sm">
          <report-wrapper
            reportId="38"
            [filters]="report.queryFilters"
            [visibleColumns]="report.visibleColumns">
          </report-wrapper>
        </div>
      </div>
      <h3 *ngIf="loading">
        <activity-indicator useDefaultText="true"></activity-indicator>
      </h3>
    `
})
export class WebshopAccessSettingsComponent implements OnInit, OnDestroy {
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IWebshopAccessSettings = {
    userApproval: undefined,
    genericUser: undefined,
    emailSuffix: undefined,
    signupLink: undefined,
    ticketLink: undefined,
    company: undefined,
    approver: undefined,
    defaultGenericUserName: undefined,
    webshopId: this.webshopService.getWebshopId()
  };

  public report:{
    queryFilters?:any;
    visibleColumns?:string[];
  };
  public loading:boolean;

  private readonly:boolean;
  private canRecreateGenericUsers:boolean;
  private genericUserField:IExtraFieldConfigurationObject;
  private userAdministratorField:IExtraFieldConfigurationObject;
  private companySSOField:IExtraFieldConfigurationObject;
  private userApprovalRadioField:IExtraFieldConfigurationObject;
  private ssoSectionField:IExtraFieldConfigurationObject;
  private recreateGenericUsersField:IExtraFieldConfigurationObject;
  private valueOnChangeSubscription: Subscription;
  private wizard:GaWizard;
  private emailSuffixDialog:IDialog;
  private isEmailSuffixDialogOpen:boolean;

  constructor(
    private authService:AuthService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private gaEventService:GaEventService,
    private messageService:GaMessageService,
    private translationService:TranslationService,
    private userDataService:UserDataService,
    private validateService:GaValidateService,
    private webshopService:WebshopService,
    private webshopAccessSettingsDataService:WebshopAccessSettingsDataService,
    private wizardService:GaWizardService
  ) {
    this.wizard = this.wizardService.getInstance(WIZARD_WEBSHOP);
  }

  public ngOnInit():void {
    this.loading = true;
    this.readonly = !this.authService.viewWrite(SEC_WS_ACCESS);
    this.canRecreateGenericUsers = this.authService.viewWrite(SEC_WS_RECREATEGENERICUSERS);

    // initial report default configuration
    this.initReportConfiguration();

    // register wizard actions button and validator
    this.initWizardSetup();

    // create all formly fields
    this.fields = this.createFields();

    // init model by getting access settings data through server request
    this.webshopAccessSettingsDataService.getAccessSettings(this.webshopService.getWebshopId())
      .then((res:IWebshopAccessSettings) => {
        this.model = res;
        // set copy to clipboard add-on value
        this.companySSOField.templateOptions.addOnButton.inputs.value = this.model.company?.urlSSOLogin;
        this.ssoSectionField.templateOptions.inputs.useSSO = this.model.company?.useSSO;

        // get external ticket link
        this.getExternalTicketLink();

        // fill necessary fields option list
        if (!this.readonly) {
          this.fillFields();

          this.updateFieldsStatus(this.model?.userApproval);
          this.updateUserAdministratorInfoField();
        }
      })
      .finally(() => {
        this.loading = false;
      });

    // register listener
    this.listen();
  }

  private listen(): void {
    this.valueOnChangeSubscription = this.form.valueChanges.subscribe(() => {
      if (this.userApprovalRadioField) {
        this.userApprovalRadioField.templateOptions.inputs.model = this.model;
      }
    });
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const labelClassSignup:string = 'col-sm-3';
    const valueClassSignup:string = 'col-sm-9';
    const labelClass:string = 'col-sm-4';
    const valueClass:string = 'col-sm-8';

    const fields:IExtraFieldConfigurationObject[] = [];

    /* ------ SIGNUP ------ */
    fields.push(
      this.formlyService.aTitle('WEBSHOP.ACCESS_SETTINGS.SIGNUP_TITLE')
        .build());

    if (this.readonly) {
      fields.push(
        this.formlyService.aValue('emailSuffix', 'WEBSHOP.ACCESS_SETTINGS.EMAILSUFFIX.LABEL')
          .isRequired()
          .withLabelClass(labelClassSignup)
          .withValueClass(valueClassSignup)
          .build());
    } else {
      fields.push(
        this.formlyService.anInput('emailSuffix', 'WEBSHOP.ACCESS_SETTINGS.EMAILSUFFIX.LABEL')
          .isRequired()
          .withLabelClass(labelClassSignup)
          .withValueClass(valueClassSignup)
          .withAddOnButton({
            component: ButtonAddonComponent,
            inputs: {
              label: 'GA.BUTTONS.TEST',
              busy: () => this.isEmailSuffixDialogOpen,
              disable: () => !this.model.emailSuffix
            },
            outputs: {
              onClick: () => this.showEmailSuffixPopup()
            }
          })
          .build()
      );
    }

    fields.push(
      this.formlyService.aValue('signupLink')
        .withLabelClass(labelClassSignup)
        .withValueClass(valueClassSignup)
        .withClipboard()
        .build(),
      this.formlyService.aValue('ticketLink')
        .withLabelClass(labelClassSignup)
        .withValueClass(valueClassSignup)
        .withClipboard()
        .build(),

      /* ------ USER APPROVAL ------ */
      this.formlyService.aTitle('WEBSHOP.ACCESS_SETTINGS.USER_APPROVAL_TITLE')
        .build()
    );

    if (this.readonly) {
      fields.push(
        this.formlyService.aGroup()
          .withoutWrapper()
          .withWrapperClass('col-md-12')
          .withHideExpression(() => !this.model?.userApproval)
          .withFields([
            this.formlyService.aCustom()
              .withComponent(UserManualApprovalFieldComponent)
              .withLabelClass('d-none')
              .build(),
            this.formlyService.aValue('approver', 'WEBSHOP.ACCESS_SETTINGS.APPROVER.LABEL')
              .withLabelClass(labelClassSignup)
              .withValueClass(valueClassSignup)
              .withFormat(this.formattingService.formatContact)
              .build()
            ]
          )
          .build(),

        this.formlyService.aGroup()
          .withoutWrapper()
          .withWrapperClass('col-sm-12')
          .withHideExpression(() => this.model?.userApproval)
          .withFields([
            this.formlyService.aCustom()
              .withComponent(UserAutoApprovalFieldComponent)
              .withLabelClass('d-none')
              .build(),
            this.formlyService.aValue('genericUser', 'WEBSHOP.ACCESS_SETTINGS.GENERICUSER.LABEL')
              .withLabelClass(labelClass)
              .withValueClass(valueClass)
              .withFormat(this.formattingService.formatLov)
              .build()
          ])
          .build()
      );

    } else {
      fields.push(
        // we use custom field to achieve preferred layout
        // see reference at https://acslworld.atlassian.net/wiki/spaces/DEV/pages/692584474/Webshop+Access+Settings
        this.userApprovalRadioField = this.formlyService.aCustom()
          .withLabelClass('d-none')
          .withValueClass('col-sm-12')
          .withComponent(UserApprovalRadioFieldsComponent)
          .withExtraOptions({
            templateOptions: {
              inputs: {
                model: this.model
              },
              outputs: {
                onChange: this.updateFieldsStatus
              }
            }
          })
          .build(),

        this.formlyService.aGroup()
          .withLabelClass('d-none')
          .withValueClass('col-sm-12')
          .withFields([
            this.userAdministratorField = this.formlyService.aSearch('approver', 'WEBSHOP.ACCESS_SETTINGS.APPROVER.LABEL')
              .withLabelClass(labelClass)
              .withValueClass(valueClass)
              .withWrapperClass('col-sm-6')
              .withFormat(this.formattingService.formatContact)
              .withExtraOptions({
                templateOptions: {
                  searchFunction: {
                    request: (filter:string) => this.webshopAccessSettingsDataService.searchUserAdministrator(this.webshopService.getWebshopId(), filter)
                  }
                },
                expressionProperties: {
                  'templateOptions.required': 'model.userApproval',
                  'templateOptions.disabled': '!model.userApproval'
                }
              })
              .onChange(this.updateUserAdministratorInfoField)
              .build(),
            this.genericUserField = this.formlyService.aSelect('genericUser', 'WEBSHOP.ACCESS_SETTINGS.GENERICUSER.LABEL')
              .withLabelClass(labelClass)
              .withValueClass(valueClass)
              .withWrapperClass('col-sm-6')
              .withFormat(this.formattingService.formatLov)
              .withExtraOptions({
                expressionProperties: {
                  'templateOptions.required': '!model.userApproval',
                  'templateOptions.disabled': 'model.userApproval'
                }
              })
              .build()
          ])
          .build()
      );
    }

    fields.push(
      /* ------ SSO ------ */
      this.formlyService.aTitle('SSO')
        .build(),
      this.ssoSectionField = this.formlyService.aCustom()
        .withLabelClass('d-none')
        .withValueClass('col-sm-12')
        .withComponent(SSOSectionFieldsComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              useSSO: this.model?.company?.useSSO
            }
          }
        })
        .build(),

      this.companySSOField = this.formlyService.aValue('company')
        .withEditable('ssoLoginUrl')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat((company:ICompany) => {
          return company?.urlSSOLogin;
        })
        .withAddOnButton({
          component: CopyToClipboardComponent,
          inputs: {
            value: this.model?.company?.urlSSOLogin
          }
        })
        .withHideExpression(() => !this.model.company || (this.model.company && !this.model.company.useSSO))
        .build(),

      /* ------ GENERIC USERS ------ */
      this.formlyService.aTitle('WEBSHOP.ACCESS_SETTINGS.GENERIC_USERS_TITLE')
        .build()
    );

    if (this.canRecreateGenericUsers) {
      fields.push(
        this.formlyService.anInput('defaultGenericUserName')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withWrapperClass('col-sm-6')
        .build(),
        this.recreateGenericUsersField = this.formlyService.aLink('WEBSHOP.ACCESS_SETTINGS.RECREATE_GENERIC_USER.TEXT')
          .withLabelClass('d-none')
          .withValueClass('d-inline-block')
          .withoutWrapper()
          .withWrapperClass('col col-auto me-auto ps-0')
          .withGroupClass('no-margin-bottom')
          .withExtraOptions({
            templateOptions: {
              linkClass: 'btn btn-default no-margin'
            }
          })
          .onClick(this.recreateGenericUser)
          .build()
      );
    }

    return fields;
  }

  private showEmailSuffixPopup = ():void => {
    const closeDialog = (): void => {
      this.emailSuffixDialog.hide();
      this.isEmailSuffixDialogOpen = false;
    };

    this.isEmailSuffixDialogOpen = true;
    this.emailSuffixDialog = this.messageService.showDialog({
      component: EmailTesterPopupComponent,
      wrapperComponent: false,
      inputs: {
        webshopId: this.webshopService.getWebshopId(),
        emailSuffix: this.model.emailSuffix
      },
      outputs: {
        onSuccess: (response:string) => {
          // close dialog then show feedback message
          closeDialog();

          if (response === 'VALID') {
            this.messageService.showFeedbackMessage('WEBSHOP.ACCESS_SETTINGS.EMAILSUFFIX_VALID');
          } else {
            this.messageService.showFeedbackMessage('WEBSHOP.ACCESS_SETTINGS.EMAILSUFFIX_INVALID', MessageType.DANGER);
          }
        },
        onCancel: () => {
          closeDialog();
        }
      },
      size: DialogSize.MD
    });
  };

  private getExternalTicketLink():void {
    this.webshopAccessSettingsDataService.getExternalTicketLink(this.model.company?.id)
      .then((ticketLink:string) => {
        this.form.patchValue({ ticketLink });
      });
  }

  private updateUserAdministratorInfoField = ():void => {
    const email:string = this.model?.approver?.email;

    this.userAdministratorField.templateOptions.info = email;
    this.userAdministratorField.templateOptions.showInfo = !!email;
  };

  private updateFieldsStatus = (userApproval:boolean):void => {
    if (userApproval) {
      this.userAdministratorField.templateOptions.disabled = false;
      this.genericUserField.templateOptions.disabled = true;

      this.userAdministratorField.templateOptions.labelNgClass = { 'disabled-control': false };
      this.genericUserField.templateOptions.labelNgClass = { 'disabled-control': true };

    } else if (util.notNullOrUndefinedOrEmpty(userApproval)) {
      this.userAdministratorField.templateOptions.disabled = true;
      this.genericUserField.templateOptions.disabled = false;

      this.userAdministratorField.templateOptions.labelNgClass = { 'disabled-control': true };
      this.genericUserField.templateOptions.labelNgClass = { 'disabled-control': false };
    }
  };

  private initReportConfiguration():void {
    this.report = {
      queryFilters: {
        Desprnm: this.webshopService.getWebshopId()
      },
      visibleColumns: this.readonly?
        [
          'Defname',
          'Deusrnm',
          'Deinsal',
          'Devieal',
          'Dehidpr'
        ] :
        [
          'Defname',
          'Deusrnm',
          'Deinsal',
          'Devieal',
          'Dehidpr',
          'Permission'
        ]
    };
  }

  private initWizardSetup():void {

    if (!this.readonly) {
      // set the current validator to be able to call validateCurrent() in the parent component
      this.wizard.currentValidator(this.validateForm, true);
      this.wizard.addAction(
        this.wizardService.createActionButton(
          SAVE_ACCESS_ACTION,
          this.translationService.instant('GA.BUTTONS.SAVE_LABEL', { label: this.translationService.instant('WEBSHOP.TABS.ACCESS') }),
          'btn btn-success',
          this.saveAccessSettings
        )
      );
    }
  }

  private fillFields():void {
    this.formlyService.fillField(this.genericUserField, this.userDataService.getGenericUsers(this.model.company.id, this.webshopService.getWebshopId()));
  }

  public recreateGenericUser = ():void => {
    if (!this.model.defaultGenericUserName) {
      this.messageService.showErrorDialog(
        'Error',
        this.translationService.instant('GA.FORM.REQUIRED', { label: this.translationService.instant('WEBSHOP.ACCESS_SETTINGS.DEFAULTGENERICUSERNAME.LABEL') })
      );
      return;
    }

    this.recreateGenericUsersField.templateOptions.busy = true;
    this.webshopAccessSettingsDataService.recreateGenericUsers(this.webshopService.getWebshopId(), this.model.defaultGenericUserName)
      .then(() => {
        // refresh report row
        this.gaEventService.sendMessage(EVENT_REPORT_WRAPPER_REFRESH);

        // get generic users list to be use for selecting default generic user
        // need to clear cache first, so it can reload data
        this.userDataService.clear();
        this.fillFields();
      })
      .finally(() => {
        this.recreateGenericUsersField.templateOptions.busy = false;
      });
  };

  private saveAccessSettings = ():void => {
    this.validateForm()
      .then(() => {
        // set busy status of wizard button
        this.updateWizardButtonStatus(true);

        const requestData:IWebshopAccessSettingsRequest = _.omit(util.deepClone(this.model), [
          'signupLink',
          'company',
          'ticketLink'
        ]);

        this.webshopAccessSettingsDataService.saveAccessSettings(this.webshopService.getWebshopId(), requestData)
          .then((res:IWebshopAccessSettings) => {
            this.model = res;

            this.messageService.showFeedbackAfterSave('WEBSHOP.TABS.ACCESS');
            if (this.webshopService.onSaved) {
              this.webshopService.onSaved.emit(this.webshopService.getWebshopId());
            }
          })
          .finally(() => {
            // re-enable busy status of wizard button
            this.updateWizardButtonStatus(false);
          });
      });
  };

  private updateWizardButtonStatus(busy:boolean):void {
    this.wizard.setActionBusy(SAVE_ACCESS_ACTION, busy);
    this.wizard.setActionEnabled(SAVE_ACCESS_ACTION, !busy);
  }

  private validateForm = ():Promise<void|string[]> => {
    return this.validateService.validateFields(
      this.fields,
      this.model,
      true
    );
  };

  public ngOnDestroy():void {
    this.userDataService.clear();
    // remove action button when wizard step leave from this component
    this.wizard.removeAction(SAVE_ACCESS_ACTION);
    this.wizard.clearCurrentValidator();
    // unsub value on change subscription
    this.valueOnChangeSubscription.unsubscribe();
  }
}